import { computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';

import { api } from '@/shared/api';
import { sentryModel, useUserStore } from '@/shared/model';
import { bloomreachLib } from '@/shared/lib';

export const ACCOUNT_QUERY_KEY = ['account'];

/**
 * Query function behind `useAccount`, exported for imperative
 * `queryClient.fetchQuery` calls (with `ACCOUNT_QUERY_KEY` and
 * `staleTime: Infinity` they share the same cache entry).
 */
export const fetchAccountData = () =>
  useUserStore()
    .getUserId()
    .then(userId => api.accounts.fetchAccount(userId))
    .then(({ data }) => {
      bloomreachLib.identify(
        { userId: data.id, email: data.email },
        { birthDate: data.dob ?? undefined },
      );

      return {
        passengers: data.passengers,
        contacts: {
          email: data.email,
          phone: data.phone,
        },
        person: {
          firstName: data.first_name,
          lastName: data.last_name,
          dob: data.dob,
        },
      };
    })
    .catch(error => {
      sentryModel.captureException(
        sentryModel.getReadableError(
          sentryModel.createCustomErrorText(
            sentryModel.errorPrefix.DEFAULT,
            'FETCH_ACCOUNT',
          ),
          error,
        ),
      );
      throw error;
    });

export const useAccount = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ACCOUNT_QUERY_KEY,
    queryFn: fetchAccountData,
    /** Since account data never changes after creation there is no need to try to update it */
    staleTime: Infinity,
  });

  return {
    data,
    isLoading,
    isError,
  };
};

/**
 * Thin selector over `useAccount` that exposes the account's contacts
 * (phone + email) plus an `isReady` flag. Use this from any feature that
 * needs the authenticated user's phone or email — recommended offers,
 * analytics events, the contacts-form hydration watcher — so consumers
 * don't have to redo the empty-string coercion or the vue-query "is the
 * data loaded yet" check.
 */
export const useAccountContacts = () => {
  const { data, isLoading, isError } = useAccount();

  const phone = computed(() => data.value?.contacts.phone ?? '');
  const email = computed(() => data.value?.contacts.email ?? '');
  const isReady = computed(() => data.value !== undefined);

  return { phone, email, isReady, isLoading, isError };
};
